// src/components/Experiment/AnalysisResultCard.js
import React, { useState } from 'react';
import {
  Card, Badge, Button, Collapse, Table, Row, Col,
  Dropdown, Modal, Spinner, Alert, ProgressBar, Tabs, Tab, OverlayTrigger, Tooltip, Accordion
} from 'react-bootstrap';
import VisualizationCard from './VisualizationCard';
import InferenceResultCard from './InferenceResultCard';
import HelpLink from '../HelpLink';
import { METHOD_DOCS } from '../../constants/docsLinks';
import './AnalysisResultCard.css';

// Docs sections the "?" buttons of the classification block open
const METRICS_DOC = '/chemometrics/results/metrics';
// Docs pages the "?" buttons of the Details and Config tabs open
const VALIDATION_DOC = '/chemometrics/running-analysis/validation';
const NESTED_DOC = '/chemometrics/running-analysis/nested-validation';
const FEATURE_SELECTION_DOC = '/chemometrics/running-analysis/feature-selection';
const REPLICATES_DOC = '/chemometrics/running-analysis/replicates';
const PERMUTATION_DOC = '/chemometrics/running-analysis/permutation-test';
const FEATURE_IMPORTANCE_DOC = '/chemometrics/visualizations/feature-importance';

// Full name (abbreviations spelled out), short description and docs section of every
// metric label of the classification block. Texts that depend on the analysis
// (positive / negative class) are functions of ctx = { positive, negative }.
const METRIC_HELP = {
  cv_accuracy: {
    term: 'Cross-validated (CV) accuracy',
    text: 'Share of rows classified correctly by models that did not see them in fitting. Badge: mean over the folds; interval: pooled out-of-fold predictions, Clopper–Pearson.',
    doc: `${METRICS_DOC}#accuracy-clopperpearson`,
  },
  cv_balanced: {
    term: 'Cross-validated balanced accuracy',
    text: 'Mean of the recalls of the classes over the pooled out-of-fold predictions: every class counts equally, whatever its size (chance level = 1 / number of classes). Interval: MOVER from the Clopper–Pearson intervals of the classes.',
    doc: `${METRICS_DOC}#balanced-accuracy-and-macro-precision-mover`,
  },
  cv_precision: {
    term: 'Cross-validated precision (macro average)',
    text: 'Of the rows predicted as a class, the share that really belongs to it, averaged over the classes. Badge: mean over the folds; below: pooled value with its interval (MOVER).',
    doc: `${METRICS_DOC}#classification-metrics`,
  },
  cv_auc: {
    term: 'Cross-validated AUC — Area Under the ROC (Receiver Operating Characteristic) Curve, mean over the folds',
    text: 'Probability that a random positive row gets a higher score than a random negative one: 0.5 = chance, 1 = perfect ranking. Needs both classes in a fold; with one sample per fold use CV AUC Agg.',
    doc: `${METRICS_DOC}#three-ways-to-combine-folds`,
  },
  cv_auc_agg: {
    term: 'Aggregated (pooled) cross-validated AUC — Area Under the ROC Curve',
    text: 'AUC of the scores of all test folds put together, every row once. Interval: Newcombe (2006) score interval, informative also at AUC = 1.',
    doc: `${METRICS_DOC}#auc-newcombe`,
  },
  cv_r2: {
    term: 'Q² — cross-validated coefficient of determination (R²)',
    text: 'Computed from the 0/1 codes of the two classes (PLS-DA): 1 = no errors; each error lowers it; can be negative.',
    doc: `${METRICS_DOC}#pls-da-r-and-q`,
  },
  cv_sensitivity: {
    term: 'Cross-validated sensitivity (true positive rate, recall of the positive class)',
    text: (c) => `Share of the positive class (${c.positive}) predicted correctly, pooled out-of-fold predictions; k/n = correct / rows of the class. Interval: Clopper–Pearson.`,
    doc: `${METRICS_DOC}#sensitivity-specificity-and-precision`,
  },
  cv_specificity: {
    term: 'Cross-validated specificity (true negative rate)',
    text: (c) => `Share of the negative class (${c.negative}) not assigned to the positive class, pooled out-of-fold predictions; k/n = correct / rows of the class. Interval: Clopper–Pearson.`,
    doc: `${METRICS_DOC}#sensitivity-specificity-and-precision`,
  },
  cv_recall: {
    term: 'Cross-validated recall (macro average)',
    text: 'Of the rows of a class, the share predicted as that class, averaged over the classes; mean over the folds. The recall of every class with its interval is in Class Metrics.',
    doc: `${METRICS_DOC}#classification-metrics`,
  },
  cv_f1: {
    term: 'Cross-validated F1-score (macro average)',
    text: 'Harmonic mean of precision and recall, averaged over the classes. Badge: mean over the folds; below: pooled value. F1 is not a proportion and has no exact analytical interval: use the Bootstrap CI plot.',
    doc: `${METRICS_DOC}#f1-no-analytical-interval`,
  },
  cv_folds: {
    term: 'Number of cross-validation folds',
    text: 'Train/test splits that ran. With Group K-Fold and one sample per fold (leave-one-sample-out) it equals the number of samples.',
    doc: '/chemometrics/running-analysis/validation',
  },
  fold_scores: {
    term: 'Per-fold values',
    text: 'The metric of every test fold (hover a badge for its number). Folds without rows of a class have no sensitivity or specificity and are not shown.',
    doc: `${METRICS_DOC}#three-ways-to-combine-folds`,
  },
  class_recall: {
    term: 'Recall of the class (sensitivity of this class)',
    text: 'Share of the rows of the class predicted as the class; k/n with the Clopper–Pearson interval.',
    doc: `${METRICS_DOC}#sensitivity-specificity-and-precision`,
  },
  class_specificity: {
    term: 'Specificity of the class (true negative rate)',
    text: 'Share of the rows of the other classes not assigned to this class; k/n with the Clopper–Pearson interval.',
    doc: `${METRICS_DOC}#sensitivity-specificity-and-precision`,
  },
  class_precision: {
    term: 'Precision of the class (positive predictive value)',
    text: 'Share of the rows assigned to the class that really belong to it; k/n with the Clopper–Pearson interval.',
    doc: `${METRICS_DOC}#sensitivity-specificity-and-precision`,
  },
  class_f1: {
    term: 'F1-score of the class',
    text: 'Harmonic mean of the precision and recall of the class, 2TP / (2TP + FP + FN); no analytical interval.',
    doc: `${METRICS_DOC}#f1-no-analytical-interval`,
  },
  train_accuracy: {
    term: 'Training accuracy (self-test)',
    text: 'Share of rows classified correctly by the final model on the rows it was fitted on. Always optimistic.',
    doc: `${METRICS_DOC}#cross-validated-vs-training-metrics`,
  },
  train_precision: {
    term: 'Training precision (macro average, self-test)',
    text: 'Precision of the final model on its own training rows, averaged over the classes.',
    doc: `${METRICS_DOC}#cross-validated-vs-training-metrics`,
  },
  train_r2: {
    term: 'R² — coefficient of determination (training)',
    text: 'PLS-DA only: computed from the 0/1 class codes on the training rows. N/A for the other methods.',
    doc: `${METRICS_DOC}#pls-da-r-and-q`,
  },
  train_sensitivity: {
    term: 'Training sensitivity (true positive rate, self-test)',
    text: (c) => `Share of the positive class (${c.positive}) predicted correctly by the final model on its own training rows.`,
    doc: `${METRICS_DOC}#cross-validated-vs-training-metrics`,
  },
  train_specificity: {
    term: 'Training specificity (true negative rate, self-test)',
    text: (c) => `Share of the negative class (${c.negative}) not assigned to the positive class by the final model on its own training rows.`,
    doc: `${METRICS_DOC}#cross-validated-vs-training-metrics`,
  },
  train_recall: {
    term: 'Training recall (macro average, self-test)',
    text: 'Recall of the final model on its own training rows, averaged over the classes.',
    doc: `${METRICS_DOC}#cross-validated-vs-training-metrics`,
  },
  train_f1: {
    term: 'Training F1-score (macro average, self-test)',
    text: 'F1-score of the final model on its own training rows, averaged over the classes.',
    doc: `${METRICS_DOC}#cross-validated-vs-training-metrics`,
  },
};

// Metric label: tooltip with the full name and description, plus a "?" docs button
const MetricLabel = ({ metric, label, ctx = {}, uid, small = false }) => {
  const info = METRIC_HELP[metric];
  const text = typeof info.text === 'function' ? info.text(ctx) : info.text;
  return (
    <span className="metric-label">
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`metric-${metric}-${uid}`} className="metric-tooltip">
            <strong>{info.term}</strong>
            <div>{text}</div>
          </Tooltip>
        }
      >
        {small ? <span className="small metric-label-text">{label}</span> : <strong className="metric-label-text">{label}</strong>}
      </OverlayTrigger>
      <HelpLink path={info.doc} title={`Documentation: ${info.term}`} />
    </span>
  );
};

// Card header with a "?" docs button
const HeaderWithHelp = ({ title, doc, docTitle }) => (
  <div className="d-flex align-items-center">
    <h6 className="mb-0">{title}</h6>
    <HelpLink path={doc} title={docTitle || `Documentation: ${title}`} />
  </div>
);

// Section title inside a card body with a "?" docs button
const SectionTitleWithHelp = ({ title, doc, docTitle }) => (
  <div className="d-flex align-items-center mb-2">
    <h6 className="small fw-bold mb-0">{title}</h6>
    <HelpLink path={doc} title={docTitle || `Documentation: ${title}`} />
  </div>
);

// Analysis types whose trained model can predict unknown samples.
// Keep in sync with SUPPORTED_INFERENCE_TYPES in backend analysis/analysis.py
export const SUPPORTED_INFERENCE_TYPES = [
  'lda', 'logistic', 'svm', 'random_forest', 'knn', 'simca', 'plsda', 'xgboost', 'bbc', 'brf'
];

const AnalysisResultCard = ({
  analysis,
  onDelete,
  onCreateVisualization,
  onDeleteVisualization,
  onCreateInference,
  onDeleteInference
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');

  const getStatusBadge = (status) => {
    const variants = {
      'pending': 'secondary',
      'running': 'warning',
      'completed': 'success',
      'failed': 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getAnalysisTypeDisplay = (type) => {
    const types = {
      'pca': 'PCA',
      'lda': 'LDA',
      'logistic': 'Logistic',
      'linear': 'Linear',
      'svm': 'SVM',
      'random_forest': 'Random Forest',
      'knn': 'K-Nearest Neighbors',
      'plsda': 'PLS-DA',
      'simca': 'SIMCA',
      'pipeline': 'Pipeline Model',
      'xgboost': 'Extreme Gradient Boosting',
      'bbc': 'Balanced Bagging Classifier',
      'brf': 'Balanced Random Forest'
    };
    return types[type] || type;
  };

  const formatMetricValue = (value) => {
    if (typeof value === 'number') return value.toFixed(4);
    return value;
  };

  // Docs page of the method ('linear' and 'pipeline' have none)
  const methodDoc = METHOD_DOCS[analysis.analysis_type];
  const methodName = getAnalysisTypeDisplay(analysis.analysis_type);

  const columnLettersToNumber = (colLetters) => {
    let result = 0;
    const letters = colLetters.toUpperCase();

    for (let i = 0; i < letters.length; i++) {
      result = result * 26 + (letters.charCodeAt(i) - 'A'.charCodeAt(0) + 1);
    }

    return result;
  };

  // Function expression to count columns in a single range part
  const countColumnsInRangePart = (rangePart) => {
    if (rangePart.includes(':')) {
      // Handle range like "A2:B98"
      const [startCell, endCell] = rangePart.split(':');

      // Extract column letters (letters before numbers)
      const startCol = startCell.match(/[A-Z]+/i)?.[0] || '';
      const endCol = endCell.match(/[A-Z]+/i)?.[0] || '';

      if (!startCol || !endCol) return 0;

      const startNum = columnLettersToNumber(startCol);
      const endNum = columnLettersToNumber(endCol);

      // Return count of columns in this range
      return Math.abs(endNum - startNum) + 1;
    } else {
      // Handle single cell like "AAD2"
      const colLetters = rangePart.match(/[A-Z]+/i)?.[0] || '';
      return colLetters ? 1 : 0;
    }
  };

  // Main function expression to count total columns
  const countTotalColumns = (rangeString) => {
    if (!rangeString || typeof rangeString !== 'string') return 0;

    // Split by commas and trim whitespace
    const rangeParts = rangeString.split(',').map(part => part.trim());

    // Sum columns from all range parts
    return rangeParts.reduce((total, part) => {
      return total + countColumnsInRangePart(part);
    }, 0);
  };

  const formatPercentage = (value) => `${(value * 100).toFixed(1)}%`;

  // Name of the feature behind feature_importances[idx]. With feature selection the
  // model is fitted on the selected columns only, so idx is a position among
  // results.feature_selection.selected_features, not among analysis.feature_names.
  const getImportanceFeatureName = (idx) => {
    const results = analysis.results || {};
    const selected = results.feature_selection?.selected_features;
    const originalIdx = Array.isArray(selected) &&
      selected.length === (results.feature_importances || []).length
      ? selected[idx]
      : idx;
    return analysis.feature_names?.[originalIdx] ?? `F${originalIdx + 1}`;
  };

  // Compact PCA Results
  const renderPCAResults = () => {
    const results = analysis.results;
    if (!results) return null;

    return (
      <Row className="g-2">
        <Col md={8}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <HeaderWithHelp
                title="Variance Explained"
                doc={`${METHOD_DOCS.pca}#results-and-metrics`}
                docTitle="Documentation: PCA results"
              />
            </Card.Header>
            <Card.Body className="py-2">
              <div className="variance-breakdown">
                <Row className="g-1">
                  {results.explained_variance_ratio?.slice(0, 6).map((variance, idx) => (
                    <Col key={idx} xs={4}>
                      <div className="variance-item p-2 text-center border rounded">
                        <div className="small fw-bold">PC{idx + 1}</div>
                        <Badge bg="primary" className="fs-6">{formatPercentage(variance)}</Badge>
                        <ProgressBar
                          now={variance * 100}
                          variant="primary"
                          className="mt-1"
                          style={{ height: '4px' }}
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
                {results.cumulative_variance && (
                  <div className="mt-2 pt-2 border-top">
                    <div className="d-flex justify-content-between align-items-center">
                      <small><strong>Cumulative:</strong></small>
                      <Badge bg="success" className="fs-6">
                        {formatPercentage(results.cumulative_variance[results.cumulative_variance.length - 1])}
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <h6 className="mb-0">Details</h6>
            </Card.Header>
            <Card.Body className="py-2">
              <div className="small">
                <div className="d-flex justify-content-between mb-1">
                  <span>Components:</span>
                  <strong>{results.n_components}</strong>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span>Dimensions:</span>
                  <strong>{results.transformed_data?.[0]?.length || 'N/A'}×{results.transformed_data?.length || 'N/A'}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>95% Variance:</span>
                  <strong>
                    {results.cumulative_variance ?
                      results.cumulative_variance.findIndex(v => v >= 0.95) + 1 || '>6' : 'N/A'} PCs
                  </strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  // Compact Classification Results
    // Compact Classification Results
    const renderClassificationResults = () => {
      const results = analysis.results;
      if (!results || !results.classification_report) return null;

      const report = results.classification_report;
      const classes = results.classes || Object.keys(report).filter(key => !['accuracy', 'macro avg', 'weighted avg'].includes(key));
      const isBinary = classes.length === 2;

      // Helper function to calculate metrics from confusion matrix (fallback methods)
      const calculateSpecificity = (cm, classIndex) => {
          if (!cm || !isBinary) return null;

          // For binary classification, specificity = TN / (TN + FP)
          // Assuming classes[0] is negative and classes[1] is positive
          if (classIndex === 0) {
              // For negative class: TN = cm[1][1], FP = cm[1][0]
              const tn = cm[1]?.[1] || 0;
              const fp = cm[1]?.[0] || 0;
              return fp + tn > 0 ? tn / (tn + fp) : 0;
          } else {
              // For positive class: TN = cm[0][0], FP = cm[0][1]
              const tn = cm[0]?.[0] || 0;
              const fp = cm[0]?.[1] || 0;
              return fp + tn > 0 ? tn / (tn + fp) : 0;
          }
      };

      const calculateSensitivity = (cm, classIndex) => {
          if (!cm || !isBinary) return null;

          // For binary classification, sensitivity = TP / (TP + FN)
          // Assuming classes[0] is negative and classes[1] is positive
          if (classIndex === 0) {
              // For negative class: TP = cm[0][0], FN = cm[0][1]
              const tp = cm[0]?.[0] || 0;
              const fn = cm[0]?.[1] || 0;
              return tp + fn > 0 ? tp / (tp + fn) : 0;
          } else {
              // For positive class: TP = cm[1][1], FN = cm[1][0]
              const tp = cm[1]?.[1] || 0;
              const fn = cm[1]?.[0] || 0;
              return tp + fn > 0 ? tp / (tp + fn) : 0;
          }
      };

      // Get sensitivity values from results if available, otherwise calculate from confusion matrix
      const getCvSensitivity = () => {
        // First try the new sensitivity fields
        if (results.cv_sensitivity_avg !== undefined && results.cv_sensitivity_avg !== null) {
          return results.cv_sensitivity_avg;
        } else if (results.cv_sensitivity_aggregated !== undefined && results.cv_sensitivity_aggregated !== null) {
          return results.cv_sensitivity_aggregated;
        } else if (results.cv_sensitivity !== undefined && results.cv_sensitivity !== null) {
          return results.cv_sensitivity;
        }
        // Fall back to cv_recall (macro avg recall) if available
        else if (results.cv_recall !== undefined && results.cv_recall !== null) {
          return results.cv_recall;
        }
        // Last resort: calculate from confusion matrix
        else if (results.cv_confusion_matrix) {
          return calculateSensitivity(results.cv_confusion_matrix, 1);
        }
        return null;
      };

      const getFullSensitivity = () => {
        if (results.sensitivity !== undefined && results.sensitivity !== null) {
          return results.sensitivity;
        } else if (report['macro avg']?.['recall'] !== undefined) {
          return report['macro avg']['recall'];
        } else if (results.confusion_matrix) {
          return calculateSensitivity(results.confusion_matrix, 1);
        }
        return null;
      };

      // Get specificity values from results if available, otherwise calculate from confusion matrix
      const getCvSpecificity = () => {
        if (results.cv_specificity_avg !== undefined && results.cv_specificity_avg !== null) {
          return results.cv_specificity_avg;
        } else if (results.cv_specificity_aggregated !== undefined && results.cv_specificity_aggregated !== null) {
          return results.cv_specificity_aggregated;
        } else if (results.cv_specificity !== undefined && results.cv_specificity !== null) {
          return results.cv_specificity;
        } else if (results.cv_confusion_matrix) {
          return calculateSpecificity(results.cv_confusion_matrix, 1);
        }
        return null;
      };

      const getFullSpecificity = () => {
        if (results.specificity !== undefined && results.specificity !== null) {
          return results.specificity;
        } else if (results.confusion_matrix) {
          return calculateSpecificity(results.confusion_matrix, 1);
        }
        return null;
      };

      const cvSensitivity = getCvSensitivity();
      const cvSpecificity = getCvSpecificity();
      const fullSensitivity = getFullSensitivity();
      const fullSpecificity = getFullSpecificity();

      const cvAuc = results.cv_auc ?? null;
      const cvAucScores = results.cv_auc_scores ?? null;
      const cvAucAggregated = results.cv_auc_aggregated ?? null;

      // Analytical confidence intervals (absent in analyses run before they were added;
      // per-class / balanced accuracy / precision parts - before step 4 of CI)
      const ci = results.confidence_intervals ?? null;
      const withCi = (part) => (part?.low != null ? part : null);
      const ciAccuracy = withCi(ci?.accuracy);
      const ciAuc = withCi(ci?.auc);
      const ciSensitivity = withCi(ci?.sensitivity);
      const ciSpecificity = withCi(ci?.specificity);
      const ciBalanced = withCi(ci?.balanced_accuracy);
      const ciPrecision = withCi(ci?.precision_macro);
      const positiveClass = ci?.positive_class ?? (isBinary ? classes[1] : null);
      const classCtx = { positive: String(positiveClass), negative: String(classes[0]) };
      const ciLabel = `${Math.round((ci?.level ?? 0.95) * 100)}% CI`;

      // prefix: text before the interval (e.g. "4/5 ·"); interval.n_effective < interval.n
      // (or the analysis-level unit) means replicates were not averaged
      const renderCi = (interval, tooltip, idSuffix, prefix = null) => {
        const nRows = interval.n ?? ci.n_rows;
        const nSamples = interval.n_effective ?? ci.n_samples;
        const adjusted = interval.n_effective != null ? interval.n_effective < interval.n : ci.unit === 'rows_adjusted';
        return (
          <div>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id={`ci-${idSuffix}-${analysis.id}`}>{tooltip}</Tooltip>}
            >
              <small className="text-muted">
                {prefix}{ciLabel} {formatMetricValue(interval.low)}–{formatMetricValue(interval.high)}
              </small>
            </OverlayTrigger>
            {adjusted && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`ci-${idSuffix}-unit-${analysis.id}`}>
                    Replicates not averaged: interval width is based on {nSamples} samples,
                    not {nRows} rows
                  </Tooltip>
                }
              >
                <i className="bi bi-info-circle text-muted ms-1 small"></i>
              </OverlayTrigger>
            )}
          </div>
        );
      };

      const isNum = (v) => typeof v === 'number' && !Number.isNaN(v);
      const mean = (vals) => vals.reduce((a, b) => a + b, 0) / vals.length;

      // Per-fold sensitivity / specificity from the per-fold confusion matrices: undefined
      // (null) in a fold without rows of the class. Analyses run before the fix stored 0
      // for such folds (with one sample per fold half of the folds), so the matrices are
      // used whenever they are available.
      const foldMatrices = Array.isArray(results.cv_confusion_matrices) ? results.cv_confusion_matrices : null;
      const foldRate = (hitsOf, totalOf) => (foldMatrices && isBinary
        ? foldMatrices.map((m) => {
            const total = totalOf(m);
            return total > 0 ? hitsOf(m) / total : null;
          })
        : null);
      const foldSensitivity = foldRate((m) => m[1]?.[1] ?? 0, (m) => (m[1]?.[0] ?? 0) + (m[1]?.[1] ?? 0))
        ?? results.cv_sensitivity_scores ?? null;
      const foldSpecificity = foldRate((m) => m[0]?.[0] ?? 0, (m) => (m[0]?.[0] ?? 0) + (m[0]?.[1] ?? 0))
        ?? results.cv_specificity_scores ?? null;
      const definedFolds = (scores) => (scores || []).filter(isNum);

      // Pooled per-class metrics: from the backend (with CI) or, for older analyses, from the
      // pooled confusion matrix (no CI, rows predicted as no class unknown)
      const pooledMatrix = results.cv_confusion_matrix_aggregated ?? null;
      const perClass = (() => {
        if (Array.isArray(ci?.per_class) && ci.per_class.length === classes.length) return ci.per_class;
        if (!pooledMatrix || pooledMatrix.length !== classes.length) return null;
        const total = pooledMatrix.reduce((a, row) => a + row.reduce((x, y) => x + y, 0), 0);
        const part = (k, n) => (n > 0 ? { k, n, estimate: k / n } : null);
        return classes.map((cls, i) => {
          const rowSum = pooledMatrix[i].reduce((a, b) => a + b, 0);
          const colSum = pooledMatrix.reduce((a, row) => a + (row[i] ?? 0), 0);
          const tp = pooledMatrix[i][i] ?? 0;
          return {
            class: cls,
            n: rowSum,
            recall: part(tp, rowSum),
            specificity: part(total - rowSum - (colSum - tp), total - rowSum),
            precision: part(tp, colSum),
            f1: rowSum + colSum > 0 ? (2 * tp) / (rowSum + colSum) : null,
          };
        });
      })();

      // Rows that SIMCA / soft PLS-DA assigned to no class: not in the pooled matrix
      const unassigned = (ci?.per_class && pooledMatrix && ci.per_class.length === pooledMatrix.length)
        ? ci.per_class.map((pc, i) => pc.n - pooledMatrix[i].reduce((a, b) => a + b, 0))
        : null;
      const showUnassigned = !!unassigned && unassigned.some((u) => u > 0);

      const cvBalancedAccuracy = results.cv_balanced_accuracy_aggregated
        ?? ciBalanced?.estimate
        ?? (perClass && perClass.every((pc) => pc.recall) ? mean(perClass.map((pc) => pc.recall.estimate)) : null);
      const cvF1Pooled = ci?.f1_macro ?? null;

      const cvR2 = results.cv_r2 ?? null;
      const r2 = results.r2 ?? null;

      // Get validation message if available
      const validationMessage = results.validation_message;

      return (
        <div className="classification-results">
          {validationMessage && (
            <Row className="mb-2">
              <Col>
                <Alert variant="warning" size="sm" className="py-1 px-2 mb-0">
                  <small>{validationMessage}</small>
                </Alert>
              </Col>
            </Row>
          )}

          <Row className="g-2">
            {/* Cross-Validation */}
            <Col md={4}>
              <Card className="h-100">
                <Card.Header className="py-2">
                  <HeaderWithHelp
                    title="Cross-Validation"
                    doc="/chemometrics/running-analysis/validation"
                    docTitle="Documentation: cross-validation"
                  />
                </Card.Header>
                <Card.Body className="py-2">
                  {results.cv_scores ? (
                    <>
                      <Table size="sm" className="mb-2">
                        <tbody>
                          <tr>
                            <td><MetricLabel metric="cv_accuracy" label="CV Accuracy:" uid={analysis.id} /></td>
                            <td className="text-end">
                              <Badge bg="primary">{formatMetricValue(results.cv_mean)}</Badge>
                              {ciAccuracy && renderCi(
                                ciAccuracy,
                                `Clopper–Pearson exact interval, ${ciAccuracy.k}/${ciAccuracy.n} correct out-of-fold predictions`,
                                'acc'
                              )}
                            </td>
                          </tr>
                          {isNum(cvBalancedAccuracy) && (
                            <tr>
                              <td><MetricLabel metric="cv_balanced" label="CV Balanced Acc:" uid={analysis.id} /></td>
                              <td className="text-end">
                                <Badge bg="primary">{formatMetricValue(cvBalancedAccuracy)}</Badge>
                                {ciBalanced && renderCi(
                                  ciBalanced,
                                  'MOVER interval (Zou & Donner, 2008) from the Clopper–Pearson intervals of the class recalls',
                                  'bal'
                                )}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td><MetricLabel metric="cv_precision" label="CV Precision:" uid={analysis.id} /></td>
                            <td className="text-end">
                              <Badge bg="info">
                                {isNum(results.cv_precision) ? formatMetricValue(results.cv_precision) : 'N/A'}
                              </Badge>
                              {ciPrecision && renderCi(
                                ciPrecision,
                                'Macro precision of the pooled out-of-fold predictions (the badge is the mean over folds). MOVER interval from the Clopper–Pearson intervals of the class precisions',
                                'prec',
                                `pooled ${formatMetricValue(ciPrecision.estimate)} · `
                              )}
                            </td>
                          </tr>
                          {/* CV AUC: mean over the folds (no interval) */}
                          {isBinary && cvAuc !== null && cvAuc !== undefined && (
                              <tr>
                                <td><MetricLabel metric="cv_auc" label="CV AUC:" uid={analysis.id} /></td>
                                <td className="text-end">
                                  <Badge bg="secondary">
                                    { formatMetricValue(cvAuc)}
                                  </Badge>
                                </td>
                              </tr>
                          )}
                          {isBinary && cvAucAggregated !== null && cvAucAggregated !== undefined  && (
                              <tr>
                                <td><MetricLabel metric="cv_auc_agg" label="CV AUC Agg:" uid={analysis.id} /></td>
                                <td className="text-end">
                                  <Badge bg="secondary">
                                    { formatMetricValue(cvAucAggregated)}
                                  </Badge>
                                  {ciAuc && renderCi(
                                    ciAuc,
                                    `Newcombe (2006) score interval, ${ciAuc.n_pos} vs ${ciAuc.n_neg} samples`,
                                    'auc'
                                  )}
                                </td>
                              </tr>
                          )}
                          {isBinary && cvR2 !== null && cvR2 !== undefined  && (
                              <tr>
                                <td><MetricLabel metric="cv_r2" label={<>CV R<sup>2</sup>:</>} uid={analysis.id} /></td>
                                <td className="text-end">
                                  <Badge bg="secondary">
                                    { formatMetricValue(cvR2)}
                                  </Badge>
                                </td>
                              </tr>
                          )}
                          {isBinary ? (
                              <>
                                <tr>
                                  <td><MetricLabel metric="cv_sensitivity" label="CV Sensitivity:" ctx={classCtx} uid={analysis.id} /></td>
                                  <td className="text-end">
                                    <Badge bg="danger">
                                      {isNum(ciSensitivity?.estimate ?? cvSensitivity)
                                        ? formatMetricValue(ciSensitivity?.estimate ?? cvSensitivity) : 'N/A'}
                                    </Badge>
                                    {ciSensitivity && renderCi(
                                      ciSensitivity,
                                      `Clopper–Pearson exact interval: ${ciSensitivity.k} of ${ciSensitivity.n} ${String(positiveClass)} (positive class) predicted correctly`,
                                      'sens',
                                      `${ciSensitivity.k}/${ciSensitivity.n} · `
                                    )}
                                </td>
                              </tr>
                              <tr>
                                <td><MetricLabel metric="cv_specificity" label="CV Specificity:" ctx={classCtx} uid={analysis.id} /></td>
                                <td className="text-end">
                                  <Badge bg="warning">
                                    {isNum(ciSpecificity?.estimate ?? cvSpecificity)
                                      ? formatMetricValue(ciSpecificity?.estimate ?? cvSpecificity) : 'N/A'}
                                  </Badge>
                                  {ciSpecificity && renderCi(
                                    ciSpecificity,
                                    `Clopper–Pearson exact interval: ${ciSpecificity.k} of ${ciSpecificity.n} ${String(classes[0])} (negative class) not assigned to ${String(positiveClass)}`,
                                    'spec',
                                    `${ciSpecificity.k}/${ciSpecificity.n} · `
                                  )}
                                </td>
                              </tr>
                            </>
                          ) : (
                            <tr>
                              <td><MetricLabel metric="cv_recall" label="CV Recall:" uid={analysis.id} /></td>
                              <td className="text-end">
                                <Badge bg="danger">
                                  {isNum(results.cv_recall) ? formatMetricValue(results.cv_recall) : 'N/A'}
                                </Badge>
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td><MetricLabel metric="cv_f1" label="CV F1-Score:" uid={analysis.id} /></td>
                            <td className="text-end">
                              <Badge bg="success">
                                {isNum(results.cv_f1) ? formatMetricValue(results.cv_f1) : 'N/A'}
                              </Badge>
                              {isNum(cvF1Pooled) && (
                                <div>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip id={`f1-pooled-${analysis.id}`}>
                                        Macro F1 of the pooled out-of-fold predictions (the badge is the mean over
                                        folds). F1 is not a proportion, so it has no exact analytical interval:
                                        use the Bootstrap CI visualization.
                                      </Tooltip>
                                    }
                                  >
                                    <small className="text-muted">pooled {formatMetricValue(cvF1Pooled)}</small>
                                  </OverlayTrigger>
                                </div>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td><MetricLabel metric="cv_folds" label="Folds:" uid={analysis.id} /></td>
                            <td className="text-end">
                              <Badge bg="secondary">{results.cv_scores.length}</Badge>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                      {/* Per-fold sensitivity / specificity: folds without rows of the class are skipped */}
                      {[['Fold Sensitivity', foldSensitivity, 'danger'], ['Fold Specificity', foldSpecificity, 'warning']]
                        .filter(([, scores]) => definedFolds(scores).length > 0)
                        .map(([title, scores, bg]) => (
                          <div className="mb-2" key={title}>
                            <MetricLabel metric="fold_scores" label={`${title}:`} uid={`${analysis.id}-${title}`} small />
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {scores.map((score, idx) => (
                                isNum(score) && (
                                  <Badge
                                    key={idx}
                                    bg={bg}
                                    className="score-badge small"
                                    title={`Fold ${idx + 1}: ${formatMetricValue(score)}`}
                                  >
                                    {formatMetricValue(score)}
                                  </Badge>
                                )
                              ))}
                            </div>
                          </div>
                        ))}
                      <div className="cv-scores-breakdown">
                        <MetricLabel metric="fold_scores" label="Fold Accuracy Scores:" uid={`${analysis.id}-acc`} small />
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {results.cv_scores.map((score, idx) => (
                            <Badge
                              key={idx}
                              bg="info"
                              className="score-badge small"
                              title={`Fold ${idx + 1}: ${formatMetricValue(score)}`}
                            >
                              {formatMetricValue(score)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-muted">
                      <small>No CV scores available</small>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Class Metrics (Cross-Validation, pooled out-of-fold predictions) */}
            <Col md={4}>
              <Card className="h-100">
                <Card.Header className="py-2 d-flex align-items-center">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`class-metrics-${analysis.id}`}>
                        Every class against the rest, over the pooled out-of-fold predictions:
                        recall = share of the class predicted correctly (sensitivity of this class),
                        specificity = share of the other rows not assigned to it, precision = share of
                        the rows assigned to it that belong to it. {ci?.per_class
                          ? `${ciLabel}: Clopper–Pearson.`
                          : 'Re-run the analysis to get confidence intervals.'}
                      </Tooltip>
                    }
                  >
                    <h6 className="mb-0">Class Metrics (CV, pooled)</h6>
                  </OverlayTrigger>
                  <HelpLink
                    path={`${METRICS_DOC}#sensitivity-specificity-and-precision`}
                    title="Documentation: per-class metrics and their intervals"
                  />
                </Card.Header>
                <Card.Body className="py-2" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                  {perClass ? (
                    <div className="class-metrics-table">
                      {perClass.map((pc, idx) => (
                        <div key={idx} className="class-metric-item mb-2 p-2 border rounded">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="small fw-bold text-truncate" title={String(pc.class)}>
                              {String(pc.class)}
                            </span>
                            {pc.n_samples != null && (
                              <small className="text-muted">{pc.n_samples} samples</small>
                            )}
                          </div>
                          <Table size="sm" className="mb-0">
                            <tbody>
                              {[
                                ['Recall', pc.recall, 'info', 'class_recall'],
                                ['Specificity', pc.specificity, 'warning', 'class_specificity'],
                                ['Precision', pc.precision, 'success', 'class_precision'],
                              ].map(([label, part, bg, metric]) => (
                                <tr key={label}>
                                  <td className="small">
                                    <MetricLabel metric={metric} label={`${label}:`} uid={`${analysis.id}-${idx}`} small />
                                  </td>
                                  <td className="text-end">
                                    <Badge bg={bg} className="small">
                                      {part ? formatMetricValue(part.estimate) : 'N/A'}
                                    </Badge>
                                    {part && (
                                      withCi(part)
                                        ? renderCi(
                                            part,
                                            `Clopper–Pearson exact interval, ${part.k}/${part.n}`,
                                            `${label}-${idx}`,
                                            `${part.k}/${part.n} · `
                                          )
                                        : <div><small className="text-muted">{part.k}/{part.n}</small></div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                              <tr>
                                <td className="small">
                                  <MetricLabel metric="class_f1" label="F1-Score:" uid={`${analysis.id}-${idx}`} small />
                                </td>
                                <td className="text-end">
                                  <Badge bg="secondary" className="small">
                                    {isNum(pc.f1) ? formatMetricValue(pc.f1) : 'N/A'}
                                  </Badge>
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted">
                      <small>No CV class metrics available</small>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Model self-test on estimation samples */}
            <Col md={4}>
              <Card className="h-100">
                <Card.Header className="py-2">
                  <HeaderWithHelp
                    title="Model self-test on estimation samples"
                    doc={`${METRICS_DOC}#cross-validated-vs-training-metrics`}
                    docTitle="Documentation: cross-validated vs training metrics"
                  />
                </Card.Header>
                <Card.Body className="py-2">
                  <Table size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td><MetricLabel metric="train_accuracy" label="Accuracy:" uid={analysis.id} /></td>
                        <td className="text-end">
                          <Badge bg="primary">{formatMetricValue(report.accuracy || 0)}</Badge>
                        </td>
                      </tr>
                      <tr>
                        <td><MetricLabel metric="train_precision" label="Precision:" uid={analysis.id} /></td>
                        <td className="text-end">
                          <Badge bg="info">{formatMetricValue(report['macro avg']?.['precision'] || 0)}</Badge>
                        </td>
                      </tr>
                      {isBinary ? (
                          <>
                            <tr>
                              <td><MetricLabel metric="train_r2" label={<>R<sup>2</sup>:</>} uid={analysis.id} /></td>
                              <td className="text-end">
                                <Badge bg="secondary">
                                  {isNum(r2) ? formatMetricValue(r2) : 'N/A'}
                                </Badge>
                              </td>
                            </tr>
                            <tr>
                              <td><MetricLabel metric="train_sensitivity" label="Sensitivity:" ctx={classCtx} uid={analysis.id} /></td>
                              <td className="text-end">
                                <Badge bg="danger">
                                  {isNum(fullSensitivity) ? formatMetricValue(fullSensitivity) : 'N/A'}
                                </Badge>
                              </td>
                            </tr>
                            <tr>
                              <td><MetricLabel metric="train_specificity" label="Specificity:" ctx={classCtx} uid={analysis.id} /></td>
                              <td className="text-end">
                                <Badge bg="warning">
                                  {isNum(fullSpecificity) ? formatMetricValue(fullSpecificity) : 'N/A'}
                                </Badge>
                              </td>
                            </tr>
                          </>
                      ) : (
                          <tr>
                            <td><MetricLabel metric="train_recall" label="Recall:" uid={analysis.id} /></td>
                            <td className="text-end">
                              <Badge bg="danger">{formatMetricValue(report['macro avg']?.['recall'] || 0)}</Badge>
                            </td>
                          </tr>
                      )}
                      <tr>
                        <td><MetricLabel metric="train_f1" label="F1 Score:" uid={analysis.id} /></td>
                        <td className="text-end">
                          <Badge bg="success">{formatMetricValue(report['macro avg']?.['f1-score'] || 0)}</Badge>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Cross-Validation Confusion Matrix */}
          {results.cv_confusion_matrix_aggregated && (
            <Row className="mt-2">
              <Col>
                <Card>
                  <Card.Header className="py-2">
                    <HeaderWithHelp
                      title="Cross-Validation Confusion Matrix (Aggregated)"
                      doc="/chemometrics/visualizations/confusion-matrix"
                      docTitle="Documentation: confusion matrix"
                    />
                  </Card.Header>
                  <Card.Body className="py-2">
                    <div className="small text-muted mb-1">
                      Counts of pooled out-of-fold predictions
                      {ci?.unit === 'rows_adjusted'
                        ? ` (rows: ${ci.n_rows} rows of ${ci.n_samples} samples, replicates not averaged)`
                        : ''}
                    </div>
                    <div className="confusion-matrix-compact">
                      <Table size="sm" bordered className="text-center mb-0">
                        <thead>
                          <tr>
                            <th width="20%"></th>
                            {classes.map((className, idx) => (
                              <th key={idx} className="bg-light small">P:{className}</th>
                            ))}
                            {showUnassigned && (
                              <th className="bg-light small" title="Predicted as no class (SIMCA / soft PLS-DA)">
                                Unassigned
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {results.cv_confusion_matrix_aggregated.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                              <th className="bg-light small">A:{classes[rowIdx]}</th>
                              {row.map((cell, cellIdx) => {
                                // For binary case, highlight diagonal cells (correct predictions)
                                const isDiagonal = rowIdx === cellIdx;
                                return (
                                  <td
                                    key={cellIdx}
                                    className={isDiagonal && cell > 0 ? 'table-success' : ''}
                                  >
                                    {cell}
                                  </td>
                                );
                              })}
                              {showUnassigned && <td className="text-muted">{unassigned[rowIdx]}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </div>
      );
    };
  // Compact Regression Results
  const renderRegressionResults = () => {
    const results = analysis.results;
    if (!results) return null;

    return (
      <Row className="g-2">
        <Col md={4}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <h6 className="mb-0">Performance</h6>
            </Card.Header>
            <Card.Body className="py-2 text-center">
              <div className="r2-score">
                <div className="text-primary fw-bold fs-3">{formatMetricValue(results.r2_score || 0)}</div>
                <div className="text-muted small">R² Score</div>
              </div>
              {results.cv_scores && (
                <div className="mt-2 pt-2 border-top">
                  <div className="small">
                    CV: <Badge bg="info">{formatMetricValue(results.cv_mean)} ± {formatMetricValue(results.cv_std)}</Badge>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <h6 className="mb-0">Parameters</h6>
            </Card.Header>
            <Card.Body className="py-2">
              <div className="small">
                <div className="d-flex justify-content-between mb-1">
                  <span>Intercept:</span>
                  <strong>{formatMetricValue(results.intercept)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span>Coefficients:</span>
                  <strong>{results.coefficients?.length || 0}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>CV Folds:</span>
                  <strong>{analysis.cv_folds}</strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <h6 className="mb-0">Top Features</h6>
            </Card.Header>
            <Card.Body className="py-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
              <div className="feature-coefficients">
                {results.coefficients && results.coefficients
                  .map((coef, idx) => ({ coef, idx }))
                  .sort((a, b) => Math.abs(b.coef) - Math.abs(a.coef))
                  .slice(0, 4)
                  .map(({ coef, idx }) => (
                    <div key={idx} className="feature-item mb-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="small">F{idx + 1}</span>
                        <Badge bg={coef > 0 ? 'success' : 'danger'}>
                          {formatMetricValue(coef)}
                        </Badge>
                      </div>
                      <ProgressBar
                        now={Math.abs(coef) * 100}
                        variant={coef > 0 ? 'success' : 'danger'}
                        style={{ height: '3px' }}
                      />
                    </div>
                  ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  // Compact Feature Importance
  const renderFeatureImportance = () => {
    const results = analysis.results;
    if (!results || !results.feature_importances) return null;

    return (
      <Card>
        <Card.Header className="py-2">
          <HeaderWithHelp
            title="Top Feature Importance"
            doc={FEATURE_IMPORTANCE_DOC}
            docTitle="Documentation: feature importance"
          />
        </Card.Header>
        <Card.Body className="py-2">
          <Row className="g-2">
            {results.feature_importances
              .map((importance, idx) => ({ importance, idx, name: getImportanceFeatureName(idx) }))
              .sort((a, b) => b.importance - a.importance)
              .slice(0, 6)
              .map(({ importance, idx, name }) => (
                <Col key={idx} xs={6} md={4}>
                  <div className="feature-item p-2 border rounded text-center">
                    <div className="small fw-bold" title={name}>
                      {name.length > 40 ? `${name.substring(0, 40)}...` : name}
                    </div>
                    <Badge bg="warning" className="mb-1">{formatMetricValue(importance)}</Badge>
                    <ProgressBar
                      now={importance * 100}
                      variant="warning"
                      style={{ height: '4px' }}
                    />
                  </div>
                </Col>
              ))}
          </Row>
        </Card.Body>
      </Card>
    );
  };

  // Model-specific information display
  const renderModelInfo = () => {
    const results = analysis.results;
    if (!results || !results.model_info) return null;

    const modelInfo = results.model_info;

    return (
      <Card>
        <Card.Header className="py-2">
          <h6 className="mb-0">Model Configuration</h6>
        </Card.Header>
        <Card.Body className="py-2">
          <Row className="g-2">
            {Object.entries(modelInfo).map(([key, value]) => (
              <Col key={key} xs={6} md={4}>
                <div className="model-param p-2 border rounded text-center">
                  <div className="small text-muted text-capitalize">
                    {key.replace(/_/g, ' ')}
                  </div>
                  <div className="small fw-bold">
                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
    );
  };

  // PLS-DA specific results
  const renderPLSDA_Results = () => {
    const results = analysis.results;
    if (!results) return null;

    return (
      <div>
        {renderClassificationResults()}
      </div>
    );
  };

  // SIMCA specific results
  const renderSIMCA_Results = () => {
    const results = analysis.results;
    if (!results) return null;

    return (
      <div>
        {renderClassificationResults()}
      </div>
    );
  };

  // KNN specific results
  const renderKNN_Results = () => {
    const results = analysis.results;
    if (!results) return null;

    return (
      <div>
        {renderClassificationResults()}
      </div>
    );
  };

  // Pipeline-specific results with GridSearch visualization
  const renderPipelineResults = () => {
    const results = analysis.results;
    if (!results) return null;

    const pipelineInfo = {
      'Classifier Used': results.classifier_used || 'N/A',
      'PLS Components': results.pls_n_components || 'N/A',
      'GridSearch Enabled': results.grid_search_enabled ? 'Yes' : 'No',
      'Pipeline Components': results.pipeline_components?.join(' → ') || 'N/A'
    };

    return (
      <div className="pipeline-results">
        {/* Pipeline Configuration Summary */}
        <Row className="mb-2">
          <Col>
            <Card>
              <Card.Header className="py-2">
                <h6 className="mb-0">Pipeline Configuration</h6>
              </Card.Header>
              <Card.Body className="py-2">
                <Row className="g-2">
                  {Object.entries(pipelineInfo).map(([key, value]) => (
                    <Col key={key} xs={6} md={3}>
                      <div className="pipeline-param p-2 border rounded text-center">
                        <div className="small text-muted text-capitalize">
                          {key.replace(/_/g, ' ')}
                        </div>
                        <div className="small fw-bold">
                          {value}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* PLS Variance Explained (if available) */}
        {results.explained_variance_ratio && results.explained_variance_ratio.length > 0 && (
          <Row className="mb-2">
            <Col>
              <Card>
                <Card.Header className="py-2">
                  <h6 className="mb-0">PLS Variance Explained</h6>
                </Card.Header>
                <Card.Body className="py-2">
                  <div className="variance-breakdown">
                    <Row className="g-1">
                      {results.explained_variance_ratio.slice(0, 6).map((variance, idx) => (
                        <Col key={idx} xs={4}>
                          <div className="variance-item p-2 text-center border rounded">
                            <div className="small fw-bold">LV{idx + 1}</div>
                            <Badge bg="success" className="fs-6">{formatPercentage(variance)}</Badge>
                            <ProgressBar
                              now={variance * 100}
                              variant="success"
                              className="mt-1"
                              style={{ height: '4px' }}
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                    {results.cumulative_variance && (
                      <div className="mt-2 pt-2 border-top">
                        <div className="d-flex justify-content-between align-items-center">
                          <small><strong>Cumulative Variance:</strong></small>
                          <Badge bg="primary" className="fs-6">
                            {formatPercentage(results.cumulative_variance[results.cumulative_variance.length - 1])}
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* GridSearch Results */}
        {results.grid_search && results.grid_search.enabled && (
          <Row className="mb-2">
            <Col>
              <Card>
                <Card.Header className="py-2">
                  <h6 className="mb-0">GridSearch Optimization</h6>
                </Card.Header>
                <Card.Body className="py-2">
                  <Accordion>
                    {/* GridSearch Configuration */}
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>GridSearch Configuration</Accordion.Header>
                      <Accordion.Body className="py-2">
                        <Row className="g-2">
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Scoring Metric:</strong>
                              <Badge bg="info" className="ms-2">{results.grid_search.scoring_metric}</Badge>
                            </div>
                            <div className="mb-2">
                              <strong>Inner CV Method:</strong>
                              <Badge bg="secondary" className="ms-2">{results.grid_search.inner_cv_method}</Badge>
                            </div>
                            <div>
                              <strong>Inner CV Folds:</strong>
                              <Badge bg="secondary" className="ms-2">{results.grid_search.inner_cv_folds}</Badge>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Outer CV Method:</strong>
                              <Badge bg="primary" className="ms-2">{analysis.cv_method}</Badge>
                            </div>
                            <div className="mb-2">
                              <strong>Outer CV Folds:</strong>
                              <Badge bg="primary" className="ms-2">{analysis.cv_folds}</Badge>
                            </div>
                          </Col>
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>

                    {/* Best Parameters Summary */}
                    {results.grid_search.best_params_summary && (
                      <Accordion.Item eventKey="1">
                        <Accordion.Header>Best Parameters Summary</Accordion.Header>
                        <Accordion.Body className="py-2">
                          {results.grid_search.best_params_summary.most_frequent &&
                           Object.keys(results.grid_search.best_params_summary.most_frequent).length > 0 && (
                            <div className="mb-3">
                              <h6 className="small fw-bold mb-2">Most Frequent Best Parameters:</h6>
                              <Table size="sm" bordered className="mb-0">
                                <tbody>
                                  {Object.entries(results.grid_search.best_params_summary.most_frequent).map(([key, value]) => (
                                    <tr key={key}>
                                      <td className="small">
                                        {key.replace('__', ' → ').replace(/_/g, ' ')}
                                      </td>
                                      <td className="small fw-bold">
                                        {String(value)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          )}
                          {(!results.grid_search.best_params_summary.most_frequent ||
                            Object.keys(results.grid_search.best_params_summary.most_frequent).length === 0) && (
                            <Alert variant="info" className="mb-0">
                              <small>No common best parameters found across folds. Each fold had different optimal parameters.</small>
                            </Alert>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    )}

                    {/* Fold-by-Fold GridSearch Results */}
                    <Accordion.Item eventKey="2">
                      <Accordion.Header>Fold-by-Fold GridSearch Results</Accordion.Header>
                      <Accordion.Body className="py-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <Table size="sm" bordered striped className="mb-0">
                          <thead>
                            <tr>
                              <th>Fold</th>
                              <th>Best Score</th>
                              <th>Best Parameters</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.grid_search.results_per_fold?.map((foldResult, idx) => (
                              <tr key={idx}>
                                <td>{foldResult.fold + 1}</td>
                                <td>
                                  <Badge bg="success">{formatMetricValue(foldResult.best_score)}</Badge>
                                </td>
                                <td>
                                  <small>
                                    {Object.entries(foldResult.best_params).map(([key, value]) => (
                                      <div key={key}>
                                        <span className="text-muted">{key.replace('__', ' → ')}:</span>{' '}
                                        <strong>{String(value)}</strong>
                                      </div>
                                    ))}
                                  </small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Classification Results */}
        {renderClassificationResults()}
      </div>
    );
  };

  // '40 s', '12 min', '2.5 h'
  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    if (seconds < 90) return `${Math.max(1, Math.round(seconds))} s`;
    if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
    return `${(seconds / 3600).toFixed(1)} h`;
  };

  // Nested cross-validation: what was chosen inside the folds, how stable the choice is
  // and three overfitting diagnostics (results.nested_cv, written by nested_adapter.py)
  const renderNestedValidation = () => {
    const nested = analysis.results?.nested_cv;
    if (!nested) return null;

    const settings = nested.settings || {};
    const gaps = nested.gaps || {};
    const final = nested.final || {};
    const tuned = nested.tuned || [];
    const chosenK = [...(nested.chosen_k || [])].sort((a, b) => a - b);
    const medianK = chosenK.length ? chosenK[Math.floor((chosenK.length - 1) / 2)] : null;
    const gap = (value) => (value === null || value === undefined
      ? 'N/A' : `${value >= 0 ? '+' : ''}${value.toFixed(3)}`);
    const innerGap = gaps.inner_minus_test;
    const innerGapVariant = innerGap > 0.2 ? 'danger' : (innerGap > 0.1 ? 'warning' : 'secondary');

    // Features chosen most often over the outer folds
    const frequent = (nested.feature_frequency || [])
      .map((share, idx) => ({ share, idx }))
      .filter(({ share }) => share > 0)
      .sort((a, b) => b.share - a.share || a.idx - b.idx)
      .slice(0, 10);

    const componentGrid = settings.model_grid || [];
    const componentShares = (nested.model_index_frequency || [])
      .map((share, idx) => ({ share, n: componentGrid[idx]?.n_components }))
      .filter(({ share, n }) => share > 0 && n !== undefined);

    const outerLoop = settings.outer_splitter === 'leave_one_sample_out'
      ? `leave-one-sample-out, ${settings.outer_folds} folds`
      : `${settings.outer_folds}-fold over samples`;

    const row = (label, value, title, doc) => (
      <div className="d-flex justify-content-between mb-1" title={title}>
        <span>
          {label}
          {doc && <HelpLink path={doc} title={`Documentation: ${label.replace(/:$/, '').toLowerCase()}`} />}
        </span>
        <strong className="text-end ms-3">{value}</strong>
      </div>
    );

    // '1 – 30 (every value)' for a full range, the list of values otherwise
    const kGrid = settings.k_grid || [];
    const kGridFull = kGrid.length > 2 && kGrid.every((k, i) => i === 0 || k === kGrid[i - 1] + 1);
    const kTried = kGridFull
      ? `${kGrid[0]} – ${kGrid[kGrid.length - 1]} (every value, ${kGrid.length} values)`
      : kGrid.join(', ');

    // Outer folds: what was chosen on the training part, metrics on the test part.
    // Analyses computed before the per-fold details were stored have no 'accuracy' in the folds.
    const folds = (nested.folds || []).filter((fold) => fold.accuracy !== undefined);
    const metric = (value) => (value === null || value === undefined ? '—' : value.toFixed(3));
    const foldBinaryColumns = [['Sens.', 'sensitivity'], ['Spec.', 'specificity'], ['AUC', 'auc']]
      .filter(([, key]) => folds.some((fold) => fold[key] !== null && fold[key] !== undefined));
    const foldColumnCount = 6 + (tuned.includes('k') ? 1 : 0) + (tuned.includes('n_components') ? 1 : 0)
      + foldBinaryColumns.length;

    return (
      <Card className="mb-2">
        <Card.Header className="py-2">
          <HeaderWithHelp
            title="Validation (nested CV)"
            doc={`${NESTED_DOC}#reading-the-result`}
            docTitle="Documentation: reading the result of nested validation"
          />
        </Card.Header>
        <Card.Body className="py-2 small">
          {row('Outer loop:', outerLoop, undefined, `${NESTED_DOC}#how-it-works`)}
          {settings.inner_folds && row(
            'Inner loop:',
            `${settings.inner_folds} folds, rule ${settings.selection_rule === 'best' ? 'best' : 'one-SE'}, metric balanced accuracy`,
            undefined,
            `${NESTED_DOC}#selection-rule`
          )}
          {tuned.includes('k') && (
            <>
              {row('Number of features tried:', kTried, undefined, `${NESTED_DOC}#settings`)}
              {row('Chosen number of features (final model):', final.k)}
              {chosenK.length > 0 && row(
                'By outer fold (min – median – max):',
                `${chosenK[0]} – ${medianK} – ${chosenK[chosenK.length - 1]}`
              )}
              {row(
                'Selection stability (Jaccard):',
                nested.stability_jaccard === null || nested.stability_jaccard === undefined
                  ? 'N/A' : nested.stability_jaccard.toFixed(2),
                'Mean overlap of the feature sets chosen in the outer folds: 1 - always the same features'
              )}
              {frequent.length > 0 && (
                <div className="mb-1">
                  <span className="d-block mb-1">Most frequently selected features (share of outer folds):</span>
                  <div className="bg-light p-1 rounded">
                    {frequent.map(({ share, idx }) => (
                      <div key={idx} className="d-flex justify-content-between">
                        <span>{analysis.feature_names?.[idx] ?? `Feature ${idx + 1}`}</span>
                        <strong>{Math.round(share * 100)}%</strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {tuned.includes('n_components') && (
            <>
              {row(
                'Chosen number of components (final model):',
                final.model_params?.n_components ?? 'N/A',
                undefined,
                `${METHOD_DOCS.plsda}#choosing-the-number-of-components`
              )}
              {componentShares.length > 0 && row(
                'Components by outer fold:',
                componentShares.map(({ share, n }) => `${n}: ${Math.round(share * 100)}%`).join(', ')
              )}
            </>
          )}

          {folds.length > 0 && (
            <Accordion className="mt-2">
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <small>Outer folds: chosen variant and test metrics ({folds.length})</small>
                </Accordion.Header>
                <Accordion.Body className="p-2">
                  <div className="text-muted mb-2">
                    In every outer fold the features{tuned.includes('n_components') ? ' and the number of components' : ''} are
                    chosen on the training part only (inner loop), then the model is tested on the
                    held-out part. BA - balanced accuracy.
                    <HelpLink
                      path={`${NESTED_DOC}#outer-folds-table`}
                      title="Documentation: outer folds table"
                    />
                  </div>
                  <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                    <Table size="sm" bordered className="mb-0">
                      <thead>
                        <tr>
                          <th>Fold</th>
                          <th title="Rows in the test part">Test rows</th>
                          {tuned.includes('k') && <th title="Number of features chosen in the inner loop">k</th>}
                          {tuned.includes('n_components') && <th title="Number of components chosen in the inner loop">Comp.</th>}
                          <th title="Inner-loop balanced accuracy of the chosen variant">Inner BA</th>
                          <th title="Balanced accuracy on the training part">Train BA</th>
                          <th title="Accuracy on the test part">Test acc.</th>
                          <th title="Balanced accuracy on the test part (not defined if the test part has one class)">Test BA</th>
                          {foldBinaryColumns.map(([label]) => (
                            <th key={label} title={`${label} on the test part`}>{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {folds.map((fold, index) => (
                          <React.Fragment key={index}>
                            <tr>
                              <td>{index + 1}</td>
                              <td>{fold.n_test}</td>
                              {tuned.includes('k') && <td>{fold.k}</td>}
                              {tuned.includes('n_components') && <td>{fold.model_params?.n_components ?? '—'}</td>}
                              <td>{metric(fold.inner_chosen_metric)}</td>
                              <td>{metric(fold.train_balanced_accuracy)}</td>
                              <td>{metric(fold.accuracy)}</td>
                              <td>{metric(fold.balanced_accuracy)}</td>
                              {foldBinaryColumns.map(([label, key]) => (
                                <td key={label}>{metric(fold[key])}</td>
                              ))}
                            </tr>
                            {tuned.includes('k') && Array.isArray(fold.features) && (
                              <tr>
                                <td colSpan={foldColumnCount} className="bg-light text-break">
                                  <span className="text-muted">Features: </span>
                                  {fold.features
                                    .map((idx) => analysis.feature_names?.[idx] ?? `Feature ${idx + 1}`)
                                    .join(', ')}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          )}

          <hr className="my-2" />
          <SectionTitleWithHelp
            title="Overfitting diagnostics (balanced accuracy)"
            doc={`${NESTED_DOC}#overfitting-diagnostics`}
            docTitle="Documentation: overfitting diagnostics"
          />
          {row(
            'Train − test:', gap(gaps.train_minus_test),
            'Training minus outer-test balanced accuracy: how much the model overfits its training rows'
          )}
          <div
            className="d-flex justify-content-between mb-1"
            title="Inner-loop estimate of the chosen variant minus the outer-test estimate: optimism of the selection. Above 0.10 the choice is unreliable."
          >
            <span>Inner − test:</span>
            <Badge bg={innerGapVariant}>{gap(innerGap)}</Badge>
          </div>
          {row(
            'Fixed features − nested:', gap(gaps.fixed_minus_nested),
            'What Simple validation would report for the final features minus the nested estimate: the selection bias'
          )}
          {innerGap > 0.1 && (
            <Alert variant="warning" className="py-1 px-2 mt-2 mb-0">
              <small>
                The inner loop was much more optimistic than the outer test: the selection is
                unstable on this data, treat the chosen features with caution.
              </small>
            </Alert>
          )}

          {((nested.warnings || []).length > 0 || (nested.unavailable_notes || []).length > 0) && (
            <>
              <hr className="my-2" />
              {(nested.warnings || []).map((warning, index) => (
                <div key={`w${index}`} className="text-warning">{warning}</div>
              ))}
              {(nested.unavailable_notes || []).map((note, index) => (
                <div key={`u${index}`} className="text-muted">{note}</div>
              ))}
            </>
          )}

          {nested.timing && (
            <>
              <hr className="my-2" />
              {row(
                'Run time:',
                `${formatDuration(nested.timing.total_seconds)}` +
                  (nested.timing.estimated_seconds !== null && nested.timing.estimated_seconds !== undefined
                    ? ` (estimated ${formatDuration(nested.timing.estimated_seconds)})` : '') +
                  `, ${(nested.timing.n_fits ?? 0).toLocaleString('en-US')} model fits`,
                undefined,
                `${NESTED_DOC}#run-time-and-limits`
              )}
            </>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Nested analysis whose permutation test was not started automatically (too long)
  const renderPermutationHint = () => {
    const hint = analysis.results?.nested_cv?.permutation_hint;
    if (!hint) return null;
    if ((analysis.visualizations || []).some((v) => v.plot_type === 'permutation_plot')) return null;
    return (
      <Alert variant="info" className="py-1 px-2 mb-2">
        <small>
          <i className="bi bi-info-circle me-1"></i>
          Permutation test: run manually (Create Visualization → Permutation Plot)
          {hint.cpu_seconds !== null && hint.cpu_seconds !== undefined &&
            `, estimated about ${formatDuration(hint.cpu_seconds)} for ${hint.n_permutations} permutations`}
          . For a nested analysis every permutation repeats the whole nested procedure.
          <HelpLink
            path={`${PERMUTATION_DOC}#nested-analyses`}
            title="Documentation: permutation test of a nested analysis"
          />
        </small>
      </Alert>
    );
  };

  const renderResultsContent = () => {
    if (analysis.status !== 'completed' || !analysis.results) {
      return null;
    }

    return (
      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-2">
        <Tab eventKey="summary" title="Summary">
          <div className="results-summary">
            {renderPermutationHint()}
            {analysis.analysis_type === 'pca' && renderPCAResults()}
            {['lda', 'logistic', 'svm', 'random_forest', 'xgboost', 'bbc', 'brf'].includes(analysis.analysis_type) && renderClassificationResults()}
            {analysis.analysis_type === 'linear' && renderRegressionResults()}
            {['random_forest', 'xgboost', 'bbc', 'brf'].includes(analysis.analysis_type) && renderFeatureImportance()}
            {analysis.analysis_type === 'knn' && renderKNN_Results()}
            {analysis.analysis_type === 'plsda' && renderPLSDA_Results()}
            {analysis.analysis_type === 'simca' && renderSIMCA_Results()}
            {analysis.analysis_type === 'pipeline' && renderPipelineResults()}
          </div>
        </Tab>

        <Tab eventKey="details" title="Details">
          {renderNestedValidation()}
          <Card>
            <Card.Header className="py-2 d-flex align-items-center">
              <h6 className="mb-0">Results (JSON)</h6>
              <HelpLink path={METRICS_DOC} title="Documentation: metrics and their fields" />
              {methodDoc && (
                <HelpLink
                  path={`${methodDoc}#results-and-metrics`}
                  title={`Documentation: ${methodName} result fields`}
                />
              )}
            </Card.Header>
            <Card.Body className="py-2">
              <pre className="mb-0 small" style={{ maxHeight: '300px', overflow: 'auto' }}>
                {JSON.stringify(analysis.results, null, 2)}
              </pre>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="config" title="Config">
          <Card>
            <Card.Body className="py-2">
              <div className="small">
                <div className="d-flex justify-content-between mb-1">
                  <span>
                    Type:
                    {methodDoc && <HelpLink path={methodDoc} title={`Documentation: ${methodName}`} />}
                  </span>
                  <strong>{methodName}</strong>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span>Data Range:</span>
                  <strong>{analysis.data_range}</strong>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span>Label Column:</span>
                  <strong>{analysis.label_column}</strong>
                </div>
                <div className="d-flex justify-content-between mb-1">
                  <span>
                    CV Method:
                    <HelpLink
                      path={`${VALIDATION_DOC}#available-schemes`}
                      title="Documentation: cross-validation schemes"
                    />
                  </span>
                  <strong>{analysis.cv_method}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>
                    CV Folds:
                    <HelpLink
                      path={`${VALIDATION_DOC}#number-of-folds`}
                      title="Documentation: number of folds"
                    />
                  </span>
                  <strong>{analysis.cv_folds}</strong>
                </div>

                {/* Pipeline-specific configuration */}
                {analysis.analysis_type === 'pipeline' && analysis.results && (
                  <>
                    <hr className="my-2" />
                    <h6 className="small fw-bold mb-2">Pipeline Configuration</h6>
                    <div className="d-flex justify-content-between mb-1">
                      <span>Classifier:</span>
                      <strong className="text-capitalize">
                        {analysis.results.classifier_used || 'N/A'}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span>PLS Components:</span>
                      <strong>{analysis.results.pls_n_components || 'N/A'}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span>GridSearch:</span>
                      <strong>
                        {analysis.results.grid_search_enabled ? (
                          <Badge bg="success">Enabled</Badge>
                        ) : (
                          <Badge bg="secondary">Disabled</Badge>
                        )}
                      </strong>
                    </div>
                    {analysis.results.grid_search && (
                      <div className="mb-1">
                        <span className="d-block mb-1">GridSearch Details:</span>
                        <div className="bg-light p-2 rounded small">
                          <div className="d-flex justify-content-between">
                            <span>Scoring Metric:</span>
                            <strong>{analysis.results.grid_search.scoring_metric}</strong>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>Inner CV Method:</span>
                            <strong>{analysis.results.grid_search.inner_cv_method}</strong>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>Inner CV Folds:</span>
                            <strong>{analysis.results.grid_search.inner_cv_folds}</strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {analysis.parameters?.average_replicates && (
                    <>
                      <hr className="my-2"/>
                      <SectionTitleWithHelp
                        title="Averaging Of Replicates"
                        doc={REPLICATES_DOC}
                        docTitle="Documentation: averaging of replicates"
                      />
                      <div className="d-flex justify-content-between mb-1">
                        <span>{analysis.parameters?.average_replicates ? "Enabled" : "Disabled"}</span>
                      </div>
                    </>
                )}
                {/* Display feature selection information */}
                {analysis.results.feature_selection && (
                    <>
                      <hr className="my-2"/>
                      <SectionTitleWithHelp
                        title="Feature Selection"
                        doc={FEATURE_SELECTION_DOC}
                        docTitle="Documentation: feature selection"
                      />
                    <div className="d-flex justify-content-between mb-1">
                      <span>
                        Method:
                        <HelpLink
                          path={`${FEATURE_SELECTION_DOC}#methods`}
                          title="Documentation: feature selection methods"
                        />
                      </span>
                      <strong className="text-capitalize">{analysis.results.feature_selection.method}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span>Features Selected:</span>
                      <strong>
                        {analysis.results.feature_selection.k === 'all'
                          ? 'All features'
                          : `${analysis.results.feature_selection.selected_features?.length || 0} of ${countTotalColumns(analysis.data_range)}`
                        }
                      </strong>
                    </div>
                    {analysis.results.feature_selection.auto && (
                      <>
                        <div className="d-flex justify-content-between mb-1">
                          <span>
                            k:
                            <HelpLink
                              path={`${NESTED_DOC}#settings`}
                              title="Documentation: number of features chosen by nested validation"
                            />
                          </span>
                          <strong>
                            auto (tried {analysis.results.feature_selection.k_min ?? 1}–{analysis.results.feature_selection.k_max ?? 'all'})
                            {' → '}{analysis.results.feature_selection.k}
                          </strong>
                        </div>
                        <div className="text-muted mb-1">
                          Nested validation: the features were re-selected in every fold; shown: final model.
                        </div>
                      </>
                    )}
                    {analysis.results.feature_selection.selected_features && analysis.feature_names && (
                      <div className="mb-1">
                        <span className="d-block mb-1">Selected Features:</span>
                        <div className="bg-light p-1 rounded small">
                          {analysis.results.feature_selection.selected_features
                            .map(idx => analysis.feature_names[idx]) // Convert index to name
                            .join(', ')}
                        </div>
                      </div>
                    )}
                    {/* Fallback if feature_names is not available */}
                    {analysis.results.feature_selection.selected_features && !analysis.feature_names && (
                      <div className="mb-1">
                        <span className="d-block mb-1">Selected Feature Indices:</span>
                        <div className="bg-light p-1 rounded small">
                          {analysis.results.feature_selection.selected_features.slice(0, 10).join(', ')}
                          {analysis.results.feature_selection.selected_features.length > 10 &&
                            ` ... (+${analysis.results.feature_selection.selected_features.length - 10} more)`
                          }
                        </div>
                      </div>
                    )}
                    {analysis.results.feature_selection.feature_scores && analysis.feature_names && (
                      <div className="mt-1">
                        <span className="d-block mb-1">Top Feature Scores:</span>
                        <div className="bg-light p-1 rounded small">
                          {analysis.results.feature_selection.feature_scores
                            .map((score, idx) => ({score, idx, name: analysis.feature_names[idx]}))
                            .sort((a, b) => b.score - a.score)
                            .slice(0, 5)
                            .map(({score, idx, name}) => (
                              <div key={idx} className="d-flex justify-content-between">
                                <span>{name}:</span>
                                <strong>{score.toFixed(4)}</strong>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Display PLS information for pipeline */}
                {analysis.analysis_type === 'pipeline' && analysis.results.pls_info && (
                  <>
                    <hr className="my-2" />
                    <h6 className="small fw-bold mb-2">PLS Information</h6>
                    <div className="d-flex justify-content-between mb-1">
                      <span>Number of Components:</span>
                      <strong>{analysis.results.pls_info.n_components}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span>Loadings Shape:</span>
                      <strong>{analysis.results.pls_info.loadings_shape[0]} × {analysis.results.pls_info.loadings_shape[1]}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span>Scores Shape:</span>
                      <strong>{analysis.results.pls_info.scores_shape[0]} × {analysis.results.pls_info.scores_shape[1]}</strong>
                    </div>
                  </>
                )}

                {/* Display model-specific parameters */}
                {analysis.results.model_info && (
                  <>
                    <hr className="my-2" />
                    {methodDoc ? (
                      <SectionTitleWithHelp
                        title="Model Parameters"
                        doc={`${methodDoc}#parameters`}
                        docTitle={`Documentation: ${methodName} parameters`}
                      />
                    ) : (
                      <h6 className="small fw-bold mb-2">Model Parameters</h6>
                    )}
                    {Object.entries(analysis.results.model_info).map(([key, value]) => (
                      <div key={key} className="d-flex justify-content-between mb-1">
                        <span className="text-capitalize">{key.replace(/_/g, ' ')}:</span>
                        <strong>
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                        </strong>
                      </div>
                    ))}
                  </>
                )}

                {/* Display feature importance if available */}
                {analysis.results.feature_importances && (
                  <>
                    <hr className="my-2" />
                    <SectionTitleWithHelp
                      title="Feature Importance"
                      doc={FEATURE_IMPORTANCE_DOC}
                      docTitle="Documentation: feature importance"
                    />
                    <div className="bg-light p-2 rounded small">
                      {analysis.results.feature_importances
                        .map((importance, idx) => ({importance, idx}))
                        .sort((a, b) => b.importance - a.importance)
                        .slice(0, 5)
                        .map(({importance, idx}) => (
                          <div key={idx} className="d-flex justify-content-between">
                            <span>{getImportanceFeatureName(idx)}:</span>
                            <strong>{importance.toFixed(4)}</strong>
                          </div>
                        ))
                      }
                      {analysis.results.feature_importances.length > 5 && (
                        <div className="text-center text-muted mt-1">
                          ... and {analysis.results.feature_importances.length - 5} more features
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    );
  };

  return (
    <>
      <Card className={`analysis-result-card compact ${analysis.status} mb-3`}>
        <Card.Header className="py-2">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <h6 className="mb-0">{analysis.name}</h6>
              {getStatusBadge(analysis.status)}
              <Badge bg="outline-secondary" className="text-dark">
                {methodName}
              </Badge>
              {methodDoc && (
                <HelpLink path={methodDoc} title={`Documentation: ${methodName}`} className="ms-0" />
              )}
              <Badge bg="outline-secondary" className="text-dark">Id: {analysis.id}</Badge>
              {analysis.results?.nested_cv && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`nested-cv-${analysis.id}`}>
                      Nested cross-validation: {(analysis.results.nested_cv.tuned || [])
                        .map((t) => (t === 'k' ? 'the number of features' : 'the number of components'))
                        .join(' and ')} chosen inside every fold. The CV metrics estimate the
                      whole procedure; see the Details tab.
                    </Tooltip>
                  }
                >
                  <Badge bg="info">Nested CV</Badge>
                </OverlayTrigger>
              )}
              {analysis.results?.nested_cv && (
                <HelpLink path={NESTED_DOC} title="Documentation: nested validation" className="ms-0" />
              )}
            </div>
            <div className="d-flex align-items-center gap-1">
              <small className="text-muted me-2">
                {new Date(analysis.created_at).toLocaleString()}
              </small>

              {/* Predict Button (inference on unknown samples) with Tooltip */}
              {analysis.status === 'completed' && onCreateInference &&
                SUPPORTED_INFERENCE_TYPES.includes(analysis.analysis_type) && (
                  <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          {analysis.has_trained_model
                            ? 'Predict classes of unknown samples'
                            : 'Rerun analysis to enable predictions'}
                        </Tooltip>
                      }
                  >
                    {/* span wrapper: a disabled button does not fire events for the tooltip */}
                    <span className="d-inline-block">
                      <Button
                          variant="outline-primary"
                          size="sm"
                          className="py-0 px-2"
                          onClick={onCreateInference}
                          disabled={!analysis.has_trained_model}
                          style={analysis.has_trained_model ? undefined : { pointerEvents: 'none' }}
                      >
                        <i className="bi bi-magic me-1"></i>
                        Predict
                      </Button>
                    </span>
                  </OverlayTrigger>
              )}

              {/* Trash Bin Button with Tooltip */}
              <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Delete Analysis</Tooltip>}
              >
                <Button
                    variant="outline-danger"
                    size="sm"
                    className="py-0 px-2"
                    onClick={() => setShowDeleteModal(true)}
                >
                  <i className="bi bi-trash"></i>
                </Button>
              </OverlayTrigger>

              {/* Options Dropdown with Tooltip */}
              <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>More Options</Tooltip>}
              >
                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" size="sm" className="py-0 px-2">
                    <i className="bi bi-three-dots"></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setShowDetails(!showDetails)}>
                      <i className={`bi bi-${showDetails ? 'eye-slash' : 'eye'} me-2`}></i>
                      {showDetails ? 'Hide' : 'Show'} Details
                    </Dropdown.Item>
                    {analysis.status === 'completed' && (
                        <Dropdown.Item onClick={onCreateVisualization}>
                          <i className="bi bi-bar-chart me-2"></i>
                          Create Visualization
                        </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </OverlayTrigger>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="py-2">
          {analysis.status === 'failed' && (
              <Alert variant="danger" className="py-2 mb-2">
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <small className="flex-grow-1">{analysis.error_message || 'Analysis failed'}</small>
                </div>
              </Alert>
          )}

          {analysis.status === 'running' && (
              <Alert variant="info" className="py-2 mb-2">
                <div className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2"/>
                  <small>Analysis in progress...</small>
                </div>
              </Alert>
          )}

          {renderResultsContent()}

          {analysis.visualizations && analysis.visualizations.length > 0 && (
              <div className="mt-3">
                <h6 className="small fw-bold mb-3">Visualizations ({analysis.visualizations.length})</h6>
                <Row className="g-3">
                  {analysis.visualizations.map(visualization => (
                      <Col key={visualization.id} xs={12} lg={6}> {/* Adjust columns as needed */}
                        <VisualizationCard
                            visualization={visualization}
                      onDelete={() => onDeleteVisualization(visualization.id)}
                    />
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {analysis.inferences && analysis.inferences.length > 0 && (
              <div className="mt-3">
                <h6 className="small fw-bold mb-3">Unknown Sample Predictions ({analysis.inferences.length})</h6>
                <Row className="g-3">
                  {analysis.inferences.map(inference => (
                      <Col key={inference.id} xs={12}>
                        <InferenceResultCard
                            inference={inference}
                            modelUpdatedAt={analysis.trained_model_updated_at}
                            onDelete={() => onDeleteInference && onDeleteInference(inference.id)}
                        />
                      </Col>
                  ))}
                </Row>
              </div>
          )}

          <Collapse in={showDetails}>
            <div className="mt-2">
              <Card className="bg-light">
                <Card.Body className="py-2">
                  <h6 className="small fw-bold mb-2">Raw Data</h6>
                  <pre className="small mb-0" style={{ maxHeight: '200px', overflow: 'auto' }}>
                    {JSON.stringify(analysis, null, 2)}
                  </pre>
                </Card.Body>
              </Card>
            </div>
          </Collapse>
        </Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} size="sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6">Delete Analysis</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <Alert variant="warning" className="py-2 mb-2">
            <small>Delete "{analysis.name}"? This cannot be undone.</small>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="secondary" size="sm" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={() => { onDelete(); setShowDeleteModal(false); }}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AnalysisResultCard;